import polyline from '@mapbox/polyline';
import type { Location } from '../models/map.model';

/**
 * Maximum valid latitude value
 */
const MAX_LATITUDE = 90;

/**
 * Maximum valid longitude value
 */
const MAX_LONGITUDE = 180;

/**
 * Checks if a location has valid coordinates
 *
 * @param location - The location to validate
 * @returns True if the location has valid coordinates
 */
export const isValidLocation = (location?: Partial<Location>): boolean =>
  !!location &&
  typeof location.latitude === 'number' &&
  typeof location.longitude === 'number' &&
  !Number.isNaN(location.latitude) &&
  !Number.isNaN(location.longitude) &&
  location.latitude >= -MAX_LATITUDE &&
  location.latitude <= MAX_LATITUDE &&
  location.longitude >= -MAX_LONGITUDE &&
  location.longitude <= MAX_LONGITUDE;

/**
 * Checks if a GeoJSON position has valid coordinates
 *
 * @param position - The GeoJSON position [longitude, latitude] to validate
 * @returns True if the position has valid coordinates
 */
export const isValidPosition = ([
  longitude,
  latitude,
]: GeoJSON.Position): boolean =>
  isValidLocation({ longitude, latitude } as Location);

/**
 * Converts a Google-style encoded polyline to an array of coordinates
 *
 * @param encoded - The encoded polyline string
 * @returns Array of [longitude, latitude] positions in GeoJSON format
 * @example
 * ```
 * // Decode a polyline string into coordinates
 * const coords = decodePolyline("_p~iF~ps|U_ulLnnqC_mqNvxq`@");
 * ```
 */
export const decodePolyline = (encoded: string): GeoJSON.Position[] => {
  // Convert the polyline directly to GeoJSON LineString
  const geoJson = polyline.toGeoJSON(encoded);
  // Return just the coordinates array
  return geoJson.coordinates;
};

/**
 * Encodes an array of coordinates into a Google-style polyline
 *
 * @param coordinates - Array of [longitude, latitude] positions in GeoJSON format
 * @returns Encoded polyline string
 * @example
 * ```
 * // Encode coordinates into a polyline string
 * const coords = [[-122.4, 37.8], [-122.5, 37.7], [-122.6, 37.6]];
 * const encoded = encodePolyline(coords);
 * ```
 */
export const encodePolyline = (coordinates: GeoJSON.Position[]): string => {
  // Create a GeoJSON LineString feature
  // Use the fromGeoJSON method to encode directly
  return polyline.fromGeoJSON({
    type: 'Feature',
    geometry: {
      type: 'LineString',
      coordinates: coordinates,
    },
    properties: {},
  });
};

/**
 * Creates a GeoJSON LineString from an array of coordinates
 *
 * @param coordinates - Array of [longitude, latitude] positions
 * @returns GeoJSON LineString feature
 */
export const createLineString = (
  coordinates: GeoJSON.Position[]
): GeoJSON.Feature<GeoJSON.LineString> => ({
  type: 'Feature',
  properties: {},
  geometry: {
    type: 'LineString',
    coordinates,
  },
});

/**
 * Creates a GeoJSON Point feature from longitude and latitude
 *
 * @param longitude - The longitude coordinate
 * @param latitude - The latitude coordinate
 * @param properties - Optional properties for the feature
 * @returns GeoJSON Point feature
 */
export const createPointFeature = (
  longitude: number,
  latitude: number,
  properties: Record<string, unknown> = {}
): GeoJSON.Feature<GeoJSON.Point> => ({
  type: 'Feature',
  properties,
  geometry: {
    type: 'Point',
    coordinates: [longitude, latitude],
  },
});

/**
 * Clamps a value between min and max bounds
 */
const clamp = (value: number, min: number, max: number): number => {
  return Math.min(Math.max(value, min), max);
};

/**
 * Calculates geographic padding for map bounds to prevent edge markers from disappearing
 * @param bounds - GeoJSON.BBox in [west, south, east, north] format
 * @param zoom - Current map zoom level
 * @param pixelBuffer - Buffer size in pixels (default: 15)
 * @returns Padded bounds in the same format
 */
export const addGeographicPadding = (
  bounds: GeoJSON.BBox,
  zoom: number,
  pixelBuffer = 15
): GeoJSON.BBox => {
  const [west, south, east, north] = bounds;

  // Skip padding for invalid zoom levels
  if (!zoom || zoom <= 0) {
    return bounds;
  }

  // Calculate geographic buffer equivalent to pixel buffer at current zoom
  // Formula: 360 degrees / (256 pixels * 2^zoom) = degrees per pixel
  const degreesPerPixel = 360 / (256 * 2 ** zoom);
  const geographicBuffer = degreesPerPixel * pixelBuffer;

  // Apply padding and clamp to valid GeoJSON bounds
  const paddedWest = clamp(
    west - geographicBuffer,
    -MAX_LONGITUDE,
    MAX_LONGITUDE
  );
  const paddedSouth = clamp(
    south - geographicBuffer,
    -MAX_LATITUDE,
    MAX_LATITUDE
  );
  const paddedEast = clamp(
    east + geographicBuffer,
    -MAX_LONGITUDE,
    MAX_LONGITUDE
  );
  const paddedNorth = clamp(
    north + geographicBuffer,
    -MAX_LATITUDE,
    MAX_LATITUDE
  );

  return [paddedWest, paddedSouth, paddedEast, paddedNorth];
};

/**
 * Checks if an event contains an originalEvent (indicating user interaction)
 * This works with ViewStateChangeEvent and other map events
 * @param event - The event to check for user interaction
 * @returns boolean indicating if this was a user-initiated event
 */
export const hasOriginalEvent = (event: unknown): boolean => {
  return (
    typeof event === 'object' &&
    event !== null &&
    'originalEvent' in event &&
    (event as Record<string, unknown>).originalEvent instanceof Event
  );
};

/**
 * Validates that canvas rect has valid dimensions for map operations
 * @param rect - The canvas bounding rect to validate
 * @returns boolean indicating if rect has valid dimensions
 */
export const hasValidCanvasDimensions = (rect: {
  width: number;
  height: number;
}): boolean => {
  return rect.width > 0 && rect.height > 0;
};
